// src/components/AgGridAction/AgGridAction.jsx
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, SquarePen, Trash2, LogOut, ShieldCheck, KeyRound } from "lucide-react";

const MENU_CLOSE_EVENT = "agGridActionMenuClose";

const AgGridActionMenu = (props) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useRef(`ag-grid-action-menu-${Math.random().toString(36).slice(2)}`);

  // intenta obtener la fila (compatibilidad con distintos params)
  const getRowData = () => props.data || props.rowData || props.node?.data || props.value;

  const toggleMenu = (e) => {
    e.stopPropagation();

    if (!open) {
      document.dispatchEvent(new CustomEvent(MENU_CLOSE_EVENT, { detail: menuId.current }));
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPos({ left: rect.right, top: rect.bottom });
      }
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    // cierra si haces click fuera del botón O del menú
    const handleMouseDown = (ev) => {
      if (buttonRef.current && buttonRef.current.contains(ev.target)) return;
      if (menuRef.current && menuRef.current.contains(ev.target)) return;
      setOpen(false);
    };

    // cierra si hay scroll o resize (evita menú desubicado)
    const handleClose = () => setOpen(false);

    const handleCloseEvent = (event) => {
      if (event?.detail !== menuId.current) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener(MENU_CLOSE_EVENT, handleCloseEvent);
    window.addEventListener("scroll", handleClose, true); // true para capturar scroll en cualquier contenedor
    window.addEventListener("resize", handleClose);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener(MENU_CLOSE_EVENT, handleCloseEvent);
      window.removeEventListener("scroll", handleClose, true);
      window.removeEventListener("resize", handleClose);
    };
  }, []);

  // menu renderizado fuera del grid para evitar clipping
  const menu = (
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        transform: "translateX(-100%)", // alinea borde derecho del menú con el punto izquierdo (ajústalo si quieres)
        zIndex: 99999,
      }}
    >
      <div className="bg-white border rounded-md shadow-lg w-44 overflow-hidden">
        {props.onEdit && (
          <button
            onClick={() => {
              const row = getRowData();
              props.onEdit?.(row);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 text-left px-4 py-2 hover:bg-gray-100 text-gray-600"
          >
            <SquarePen size={16} color="#2563EB" />
            <span>Editar</span>
          </button>
        )}

        {props.onDelete && (
          <button
            onClick={() => {
              const row = getRowData();
              props.onDelete?.(row);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 text-left px-4 py-2 hover:bg-gray-100 text-gray-600"
          >
            <Trash2 size={16} color="#2563EB" />
            <span>Eliminar</span>
          </button>
        )}

        {props.onFinalize && (
          <button
            onClick={() => {
              const row = getRowData();
              props.onFinalize?.(row);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 text-left px-4 py-2 hover:bg-gray-100 text-gray-600"
          >
            <LogOut size={16} color="#2563EB" />
            <span>Salida</span>
          </button>
        )}

        {props.onViewDetails && (
          <button
            onClick={() => {
              const row = getRowData();
              props.onViewDetails?.(row);
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Ver detalles
          </button>
        )}

        {props.onGenerateTicket && (
          <button
            onClick={() => {
              const row = getRowData();
              props.onGenerateTicket?.(row);
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2 hover:bg-gray-100"
          >
            Generar ticket
          </button>
        )}

        {props.onAssignRoles && (
          <button
            onClick={() => {
              const row = getRowData();
              props.onAssignRoles?.(row);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 text-left px-4 py-2 hover:bg-gray-100 text-gray-600"
          >
            <ShieldCheck size={16} color="#2563EB" />
            <span>Asignar roles</span>
          </button>
        )}

        {props.onAssignPermissions && (
          <button
            onClick={() => {
              const row = getRowData();
              props.onAssignPermissions?.(row);
              setOpen(false);
            }}
            className="w-full flex items-center gap-2 text-left px-4 py-2 hover:bg-gray-100 text-gray-600"
          >
            <KeyRound size={16} color="#2563EB" />
            <span>Asignar permisos</span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className="p-2 rounded-full transition duration-300 hover:bg-blue-100 text-blue-800 focus:outline-none "
        aria-haspopup="true"
        aria-expanded={open}
        onMouseDown={(e) => e.stopPropagation()} // evita que AG Grid haga otras cosas
      >
        <MoreVertical size={22} />
      </button>

      {open && createPortal(menu, document.body)}
    </>
  );
};

export default AgGridActionMenu;
